import React, { useEffect, useState, useRef, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import classNames from 'classnames';

function useTargetTrigger(target) {
  const [el, setEl] = useState(null);
  useEffect(() => {
    if (!target) return;
    let found = null;
    if (typeof target === 'string') {
      found = document.getElementById(target.replace(/^#/, ''));
    } else if (target?.current) {
      found = target.current;
    } else if (target instanceof Element) {
      found = target;
    }
    setEl(found);
  }, [target]);
  return el;
}

function usePosition(triggerEl, contentRef, placement, open) {
  const [style, setStyle] = useState({ top: 0, left: 0 });
  useLayoutEffect(() => {
    if (!open || !triggerEl || !contentRef.current) return;
    const update = () => {
      const r = triggerEl.getBoundingClientRect();
      const c = contentRef.current.getBoundingClientRect();
      let top = 0;
      let left = 0;
      const gap = 8;
      switch (placement) {
        case 'top':
          top = r.top - c.height - gap;
          left = r.left + r.width / 2 - c.width / 2;
          break;
        case 'left':
          top = r.top + r.height / 2 - c.height / 2;
          left = r.left - c.width - gap;
          break;
        case 'right':
          top = r.top + r.height / 2 - c.height / 2;
          left = r.right + gap;
          break;
        case 'bottom':
        default:
          top = r.bottom + gap;
          left = r.left + r.width / 2 - c.width / 2;
      }
      setStyle({ top: top + window.scrollY, left: left + window.scrollX });
    };
    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [open, triggerEl, placement, contentRef]);
  return style;
}

function Floating({ triggerEl, placement, open, setOpen, trigger, className, children, role }) {
  const contentRef = useRef(null);
  const style = usePosition(triggerEl, contentRef, placement, open);

  useEffect(() => {
    if (!open || !triggerEl) return;
    const isClick = trigger === 'click' || trigger?.includes?.('click');
    if (!isClick) return;
    const onDocClick = (e) => {
      if (
        triggerEl.contains(e.target) ||
        contentRef.current?.contains(e.target)
      ) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open, triggerEl, trigger, setOpen]);

  if (!open) return null;

  return createPortal(
    <div
      ref={contentRef}
      role={role}
      style={{ position: 'absolute', top: style.top, left: style.left }}
      className={className}
    >
      {children}
    </div>,
    document.body
  );
}

function useTriggerEvents(triggerEl, trigger, setOpen) {
  useEffect(() => {
    if (!triggerEl) return;
    const isHover = trigger === 'hover' || trigger?.includes?.('hover');
    const isFocus = trigger === 'focus' || trigger?.includes?.('focus');
    const isClick = trigger === 'click' || trigger?.includes?.('click') || !trigger;

    const onEnter = () => setOpen(true);
    const onLeave = () => setOpen(false);
    const onClick = () => setOpen((v) => !v);

    if (isHover) {
      triggerEl.addEventListener('mouseenter', onEnter);
      triggerEl.addEventListener('mouseleave', onLeave);
    }
    if (isFocus) {
      triggerEl.addEventListener('focus', onEnter);
      triggerEl.addEventListener('blur', onLeave);
    }
    if (isClick) {
      triggerEl.addEventListener('click', onClick);
    }
    return () => {
      triggerEl.removeEventListener('mouseenter', onEnter);
      triggerEl.removeEventListener('mouseleave', onLeave);
      triggerEl.removeEventListener('focus', onEnter);
      triggerEl.removeEventListener('blur', onLeave);
      triggerEl.removeEventListener('click', onClick);
    };
  }, [triggerEl, trigger, setOpen]);
}

export function UncontrolledPopover({
  target,
  placement = 'bottom',
  trigger = 'click',
  children,
  className,
  hideArrow = false,
  ...rest
}) {
  const triggerEl = useTargetTrigger(target);
  const [open, setOpen] = useState(false);
  useTriggerEvents(triggerEl, trigger, setOpen);

  return (
    <Floating
      triggerEl={triggerEl}
      placement={placement}
      open={open}
      setOpen={setOpen}
      trigger={trigger}
      role="tooltip"
      className={classNames(
        'z-[1060] max-w-[276px] rounded border border-black/20 bg-gray-200 text-xs text-gray-600 shadow-[0_0.25rem_0.5rem_rgba(0,0,0,0.2)]',
        className
      )}
    >
      {children}
    </Floating>
  );
}

export function PopoverBody({ children, className, tag: Tag = 'div', ...rest }) {
  return (
    <Tag {...rest} className={classNames('px-3 py-2', className)}>
      {children}
    </Tag>
  );
}

export function PopoverHeader({ children, className, tag: Tag = 'h3', ...rest }) {
  return (
    <Tag
      {...rest}
      className={classNames(
        'm-0 border-b border-black/20 bg-gray-300 px-3 py-2 text-base',
        className
      )}
    >
      {children}
    </Tag>
  );
}

export function UncontrolledTooltip({
  target,
  placement = 'top',
  trigger = 'hover focus',
  children,
  className,
  ...rest
}) {
  const triggerEl = useTargetTrigger(target);
  const [open, setOpen] = useState(false);
  useTriggerEvents(triggerEl, trigger, setOpen);

  return (
    <Floating
      triggerEl={triggerEl}
      placement={placement}
      open={open}
      setOpen={setOpen}
      trigger={trigger}
      role="tooltip"
      className={classNames(
        'z-[1070] max-w-[200px] rounded bg-black px-2 py-1 text-xs text-white opacity-90',
        className
      )}
    >
      {children}
    </Floating>
  );
}
